import { useEffect, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { useGame } from './net/useGame.js';
import { applyUpdate, onUpdateReady } from './pwa.js';
import { useAccount } from './net/account.js';
import { fetchMyStats, type MyStats } from './net/stats.js';
import { Profile } from './ui/Profile.js';
import { Table } from './ui/Table.js';
import { MatchOver } from './ui/MatchOver.js';
import { LoginScreen } from './ui/LoginScreen.js';
import { Lobby } from './ui/Lobby.js';
import { WaitingTable } from './ui/WaitingTable.js';
import { Logo } from './ui/Logo.js';

/**
 * Dikey tutulan telefonda "yan çevir" uyarısı.
 *
 * Manifest'teki `orientation: landscape` Android'de kurulu uygulamada
 * çalışıyor ama iOS onu yok sayıyor ve tarayıcıda hiçbir platformda
 * uygulanmıyor. Görünürlüğü tamamen CSS'te: yalnızca DOKUNMATİK cihaz +
 * dikey yönelimde açılıyor, masaüstünde dar pencerede açılmıyor.
 */
function RotateHint() {
  return (
    <div className="rotate-hint" role="alert">
      <Logo />
      <p>Telefonu yan çevir</p>
    </div>
  );
}

/**
 * Ekran akışı: giriş → salon → (masa bekleme) → oyun.
 *
 * Giriş kapısı yalnızca istemcide; gerçek kimlik Supabase oturumu bağlanınca
 * gelecek (bkz. `LoginScreen`).
 */
export function App() {
  const game = useGame();
  /**
   * Kimlik iki kaynaktan gelebiliyor:
   *   - `account`  → Supabase oturumu (istatistikler buna işleniyor)
   *   - `guest`    → hesap sistemi yapılandırılmamış kopyada yalnızca ad
   */
  const { account, loading: accountLoading } = useAccount();
  const [guest, setGuest] = useState<string | null>(null);
  const name = account?.displayName ?? guest;

  /**
   * Masaya oturduktan sonra hangi ekran görünür.
   *
   * `atTable` görünüm gelmeden sağlanmıyor; `inRoom` ise durum 'playing'e
   * geçmiş ama görünüm henüz yokken de doğru. Bu ayrım şart: aksi hâlde
   * maç başlarken kısa bir an SALONA düşülüyordu — oyuncu "oyuna girmiyor,
   * bekliyor" diye görüyordu.
   */
  const view = game.view;
  const atTable = view !== null && (game.status === 'playing' || game.status === 'over');
  const inRoom = game.status !== 'idle' && game.status !== 'error';

  /**
   * Yeni sürüm hazırsa uygula — ama YALNIZCA masadan uzaktayken.
   *
   * Uygulamak sayfayı yeniden yüklüyor; maçın ortasında yapılsaydı bağlantı
   * kopar, koltuk bota geçerdi. Salonda ya da giriş ekranında beklemenin
   * maliyeti yok, oyuncu zaten bir sonraki masaya güncel sürümle oturuyor.
   */
  /** Profil paneli ve kendi istatistiklerim. */
  const [profileOpen, setProfileOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [myStats, setMyStats] = useState<MyStats[] | null>(null);

  useEffect(() => {
    if (!account) return;
    let disposed = false;
    void fetchMyStats().then((rows) => {
      if (!disposed) setMyStats(rows);
    });
    return () => {
      disposed = true;
    };
  }, [account, refresh, profileOpen]);

  const [updateReady, setUpdateReady] = useState(false);
  useEffect(() => {
    onUpdateReady(() => setUpdateReady(true));
  }, []);
  useEffect(() => {
    if (updateReady && !inRoom) applyUpdate();
  }, [updateReady, inRoom]);

  // Oturum diskten okunurken giriş ekranını bir an gösterip sonra atlamak
  // göz kırpması yaratıyordu; sessizce bekliyoruz.
  if (accountLoading) return <div className="gate" />;

  if (!name) return <LoginScreen onEnter={setGuest} />;

  return (
    <div className="app">
      {/* Yatay uyarısı YALNIZCA masada.
          Giriş, salon ve paneller dikeyde de sorunsuz kullanılıyor; orada
          "telefonu yan çevir" demek gereksiz bir engeldi. Yatay zorunluluğu
          yalnızca oyun masasının kendisinde: dört koltuk, orta yığın ve 13
          kartlık el dikey ekrana sığmıyor. */}
      {atTable && <RotateHint />}
      {view && atTable ? (
        <Table
          view={view}
          display={game.display}
          seats={game.seats}
          busy={game.busy}
          chat={game.chat}
          phrases={game.phrases}
          exchangeSent={game.exchangeSent}
          exchangeResult={game.exchangeResult}
          onPlay={game.play}
          onPass={game.pass}
          onExchange={game.submitExchange}
          onChat={game.sendChat}
          onQuit={game.quit}
          autoPass={game.autoPassEnabled}
          onToggleAutoPass={game.toggleAutoPass}
          isHost={game.isHost}
          mascotRun={game.mascotRun}
          onMascot={game.runMascot}
        />
      ) : inRoom ? (
        <WaitingTable
          players={game.tablePlayers}
          me={name}
          startsAt={game.startsAt}
          isHost={game.isHost}
          onBots={game.setBots}
          onLeave={game.quit}
        />
      ) : (
        <Lobby
          name={name}
          error={game.error}
          onSit={(code) => void game.connect(name, code)}
          onBots={() => game.startLocal(name)}
          username={account?.username ?? null}
          onProfile={() => setProfileOpen(true)}
          refresh={refresh}
        />
      )}

      <AnimatePresence>
        {profileOpen && account && (
          <Profile
            account={account}
            stats={myStats}
            onClose={() => setProfileOpen(false)}
            // Ad ya da şifre değişince sıralamayı ve profili tazeliyoruz.
            onChanged={() => setRefresh((n) => n + 1)}
          />
        )}
      </AnimatePresence>

      {atTable && game.error && <div className="toast">{game.error}</div>}

      {game.status === 'over' && game.view && (
        <MatchOver
          seats={game.seats}
          standings={game.standings}
          me={game.view.you}
          sold={game.sold}
          stayed={game.stayed}
          decideBy={game.decideBy}
          onStay={game.stay}
          onSell={game.sell}
        />
      )}
    </div>
  );
}
